/**
 * Cloud storage backend using server-side SQLite database.
 * Uses file-based storage: individual files synced separately for efficiency.
 * Initial load downloads complete project as ZIP for offline capability.
 */

import { StorageBackend } from './StorageBackend.js';
import JSZip from 'jszip';
import {
  sanitizeFilename,
  applyRetentionPolicy,
  shouldCreateSnapshot,
  generateSnapshotId
} from './utils.js';
import { generateExportBlob, type ExportProjectData } from '../../shared/index.js';
import type {
  ListFilesResult,
  ReadFileResult,
  ReadComponentResult,
  SaveResult,
  CreateResult,
  DeleteResult,
  RenameResult,
  SnapshotMetadata,
  Snapshot,
  SnapshotReason,
  CreateSnapshotResult,
  RestoreSnapshotResult,
  StorageMetadata,
  ProjectInfo,
  ImportResult,
  ImportOptions,
  MediaItem,
  MediaUploadResult,
  MediaUsage,
  MediaListResult,
  MediaDeleteResult,
  ProjectFileContent,
} from './types.js';

export class CloudStorageBackend extends StorageBackend {
  private token: string | null = null;
  private currentProjectId: string | null = null;
  private currentProjectName: string | null = null;

  // In-memory cache of current project's files and components
  private files = new Map<string, string>();
  private components = new Map<string, string>();
  private fileHistory = new Map<string, Snapshot[]>();

  // Cache of signed media URLs (filename -> signedUrl)
  private signedMediaUrls = new Map<string, string>();

  // Cache of checklist state (filePath -> checkedSlugs)
  private checklistCache = new Map<string, string[]>();

  constructor() {
    super('cloud');
  }

  /**
   * Set the authentication token
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Clear token and reset state (logout)
   */
  logout(): void {
    this.token = null;
    this.currentProjectId = null;
    this.currentProjectName = null;
    this.files.clear();
    this.components.clear();
    this.fileHistory.clear();
    this.signedMediaUrls.clear();
    this.checklistCache.clear();
  }

  /**
   * Helper for authenticated API calls (JSON)
   */
  private async apiFetch<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
        ...options.headers
      }
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      // Expose the HTTP status so callers can tell auth failures (401) apart
      throw Object.assign(new Error(data.error || `Request failed: ${res.status}`), { status: res.status });
    }

    return res.json();
  }

  /**
   * Helper for authenticated binary fetch
   */
  private async apiFetchBinary(url: string): Promise<Response> {
    if (!this.token) {
      throw new Error('Not authenticated');
    }

    const res = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Request failed: ${res.status}`);
    }

    return res;
  }

  // ==================== Project Management ====================

  async listProjects(): Promise<ProjectInfo[]> {
    const data = await this.apiFetch<{ projects: ProjectInfo[] }>('/api/cloud/projects');
    return data.projects;
  }

  async createProject(name: string): Promise<string> {
    const data = await this.apiFetch<{ id: string }>('/api/cloud/project', {
      method: 'POST',
      body: JSON.stringify({ name })
    });

    this.currentProjectId = data.id;
    this.currentProjectName = name;
    this.files.clear();
    this.components.clear();
    this.fileHistory.clear();

    return data.id;
  }

  async loadProject(projectId: string): Promise<ProjectInfo> {
    const res = await this.apiFetchBinary(`/api/cloud/project/${projectId}/download`);
    const projectName = res.headers.get('X-Project-Name') || 'project';
    const zipData = await res.arrayBuffer();
    const zip = await JSZip.loadAsync(zipData);

    this.currentProjectId = projectId;
    this.currentProjectName = projectName;

    this.files.clear();
    this.components.clear();
    this.fileHistory.clear();
    this.signedMediaUrls.clear();

    const loadPromises: Promise<void>[] = [];
    const historyMetaFiles: string[] = [];

    zip.forEach((relativePath, zipEntry) => {
      if (relativePath === 'project.json') return;
      if (zipEntry.dir) return;

      if (relativePath.startsWith('_components/')) {
        const name = relativePath.replace('_components/', '');
        loadPromises.push(
          zipEntry.async('string').then(content => {
            this.components.set(name, content);
          })
        );
      } else if (relativePath.startsWith('_history/') && relativePath.endsWith('.json')) {
        historyMetaFiles.push(relativePath);
      } else if (relativePath.endsWith('.src.md')) {
        loadPromises.push(
          zipEntry.async('string').then(content => {
            this.files.set(relativePath, content);
          })
        );
      }
    });

    await Promise.all(loadPromises);

    // Load file-level history snapshots (in parallel)
    const historyPromises = historyMetaFiles.map(async (metaPath) => {
      try {
        const metaFile = zip.file(metaPath);
        if (!metaFile) return;

        const metaContent = await metaFile.async('string');
        const metadata = JSON.parse(metaContent);

        const pathWithoutHistory = metaPath.replace('_history/', '').replace('.json', '');
        const lastSlashIndex = pathWithoutHistory.lastIndexOf('/');

        if (lastSlashIndex === -1) return;

        const filePath = pathWithoutHistory.substring(0, lastSlashIndex);
        const snapshotId = pathWithoutHistory.substring(lastSlashIndex + 1);

        const contentPath = `_history/${filePath}/${snapshotId}.md`;
        const contentEntry = zip.file(contentPath);

        if (!contentEntry) return;

        const content = await contentEntry.async('string');

        if (!this.fileHistory.has(filePath)) {
          this.fileHistory.set(filePath, []);
        }

        this.fileHistory.get(filePath)!.push({
          id: snapshotId,
          timestamp: metadata.timestamp,
          reason: metadata.reason,
          content
        });
      } catch (err) {
        console.warn(`Failed to load history snapshot ${metaPath}:`, err);
      }
    });
    await Promise.all(historyPromises);

    // Sort each file's history by timestamp descending
    for (const snapshots of this.fileHistory.values()) {
      snapshots.sort((a, b) => b.timestamp - a.timestamp);
    }

    // Pre-fetch media list to cache signed URLs for rendering
    try {
      await this.listMedia();
    } catch (err) {
      console.warn('Failed to pre-fetch media list:', err);
    }

    // Load checklist state for all files
    this.checklistCache.clear();
    try {
      const checklistData = await this.apiFetch<{ checklists: Array<{ filePath: string; checkedSlugs: string[] }> }>(
        `/api/cloud/project/${projectId}/checklist`
      );
      for (const entry of checklistData.checklists) {
        this.checklistCache.set(entry.filePath, entry.checkedSlugs);
      }
    } catch (err) {
      console.warn('Failed to load checklists:', err);
    }

    return { id: projectId, name: projectName };
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.apiFetch(`/api/cloud/project/${projectId}`, {
      method: 'DELETE'
    });

    if (this.currentProjectId === projectId) {
      this.currentProjectId = null;
      this.currentProjectName = null;
      this.files.clear();
      this.components.clear();
      this.fileHistory.clear();
      this.signedMediaUrls.clear();
    }
  }

  async renameProject(projectId: string, newName: string): Promise<void> {
    await this.apiFetch(`/api/cloud/project/${projectId}/rename`, {
      method: 'POST',
      body: JSON.stringify({ name: newName })
    });

    if (this.currentProjectId === projectId) {
      this.currentProjectName = newName;
    }
  }

  getCurrentProject(): ProjectInfo | null {
    if (!this.currentProjectId) return null;
    return {
      id: this.currentProjectId,
      name: this.currentProjectName ?? 'project'
    };
  }

  // ==================== StorageBackend Interface ====================

  async listFiles(): Promise<ListFilesResult> {
    if (!this.currentProjectId) {
      return { files: [], components: [] };
    }

    const formattedFiles = Array.from(this.files.keys()).map(path => {
      const parts = path.split('/');
      return {
        name: parts[parts.length - 1],
        path: path,
        dir: parts.length >= 2 ? parts[parts.length - 2] : 'uncategorized'
      };
    });

    const formattedComponents = Array.from(this.components.keys()).map(name => ({ name }));

    return { files: formattedFiles, components: formattedComponents };
  }

  async readFile(path: string): Promise<ReadFileResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const content = this.files.get(path);
    if (content === undefined) {
      throw new Error('File not found');
    }

    return {
      content,
      variables: this.extractVariables(content)
    };
  }

  async readComponent(name: string): Promise<ReadComponentResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const content = this.components.get(name);
    if (content === undefined) {
      throw new Error('Component not found');
    }

    return {
      content,
      path: `_components/${name}`,
      name
    };
  }

  async getAllContents(): Promise<ProjectFileContent[]> {
    // The whole project is downloaded on load and kept current on save
    const result: ProjectFileContent[] = [];
    for (const [path, content] of this.files) result.push({ path, content });
    for (const [name, content] of this.components) {
      result.push({ path: `_components/${name}`, content });
    }
    return result;
  }

  async saveFile(path: string, content: string): Promise<SaveResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    await this.maybeCreateSnapshot(path);

    if (path.startsWith('_components/')) {
      const name = path.replace('_components/', '');
      this.components.set(name, content);
    } else {
      this.files.set(path, content);
    }

    const response = await this.apiFetch<{ hash: string; updatedAt: string }>(
      `/api/cloud/project/${this.currentProjectId}/file`,
      {
        method: 'PUT',
        body: JSON.stringify({ path, content })
      }
    );

    return {
      success: true,
      hash: response.hash,
      updatedAt: response.updatedAt
    };
  }

  async createFile(path: string, content?: string): Promise<CreateResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const defaultContent = content || `<!-- vars
@HOSTNAME=example.com
-->
# New Document

Write your documentation here.
`;

    this.files.set(path, defaultContent);

    await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/file`, {
      method: 'PUT',
      body: JSON.stringify({ path, content: defaultContent })
    });

    return { success: true, path };
  }

  async createComponent(name: string, content?: string): Promise<CreateResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const defaultContent = content || `## Component

<!-- tangle: output.sh -->
\`\`\`bash
# Your script here
\`\`\`
`;

    const path = `_components/${name}`;
    this.components.set(name, defaultContent);

    await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/file`, {
      method: 'PUT',
      body: JSON.stringify({ path, content: defaultContent })
    });

    return { success: true, path, name };
  }

  async deleteFile(path: string): Promise<DeleteResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    await this.createSnapshot(path, 'pre_delete');

    this.files.delete(path);

    await this.apiFetch(
      `/api/cloud/project/${this.currentProjectId}/file?path=${encodeURIComponent(path)}`,
      { method: 'DELETE' }
    );

    this.fileHistory.delete(path);

    return { success: true };
  }

  async deleteComponent(name: string): Promise<DeleteResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const path = `_components/${name}`;

    await this.createSnapshot(path, 'pre_delete');

    this.components.delete(name);

    await this.apiFetch(
      `/api/cloud/project/${this.currentProjectId}/file?path=${encodeURIComponent(path)}`,
      { method: 'DELETE' }
    );

    this.fileHistory.delete(path);

    return { success: true };
  }

  async renameFile(oldPath: string, newPath: string): Promise<RenameResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const content = this.files.get(oldPath);
    if (content === undefined) {
      throw new Error('File not found');
    }

    this.files.set(newPath, content);
    this.files.delete(oldPath);

    await Promise.all([
      this.apiFetch(`/api/cloud/project/${this.currentProjectId}/file`, {
        method: 'PUT',
        body: JSON.stringify({ path: newPath, content })
      }),
      this.apiFetch(
        `/api/cloud/project/${this.currentProjectId}/file?path=${encodeURIComponent(oldPath)}`,
        { method: 'DELETE' }
      )
    ]);

    return { success: true };
  }

  async renameComponent(oldName: string, newName: string): Promise<RenameResult> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    const content = this.components.get(oldName);
    if (content === undefined) {
      throw new Error('Component not found');
    }

    const oldPath = `_components/${oldName}`;
    const newPath = `_components/${newName}`;

    this.components.set(newName, content);
    this.components.delete(oldName);

    await Promise.all([
      this.apiFetch(`/api/cloud/project/${this.currentProjectId}/file`, {
        method: 'PUT',
        body: JSON.stringify({ path: newPath, content })
      }),
      this.apiFetch(
        `/api/cloud/project/${this.currentProjectId}/file?path=${encodeURIComponent(oldPath)}`,
        { method: 'DELETE' }
      )
    ]);

    return { success: true };
  }

  async isAvailable(): Promise<boolean> {
    return !!this.token;
  }

  async getMetadata(): Promise<StorageMetadata> {
    return {
      name: this.name,
      description: 'Cloud storage (server)',
      projectId: this.currentProjectId,
      projectName: this.currentProjectName
    };
  }

  // ==================== Import/Export ====================

  async downloadProject(): Promise<{ success: boolean }> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    // Gather all files (source files + components)
    const files: Array<{ path: string; content: string }> = [];
    for (const [path, content] of this.files) {
      files.push({ path, content });
    }
    for (const [name, content] of this.components) {
      files.push({ path: `_components/${name}`, content });
    }

    // Gather history snapshots
    const history: ExportProjectData['history'] = [];
    for (const [filePath, snapshots] of this.fileHistory) {
      for (const snapshot of snapshots) {
        history.push({
          filePath,
          id: snapshot.id,
          timestamp: snapshot.timestamp,
          reason: snapshot.reason,
          content: snapshot.content,
        });
      }
    }

    // Gather media files (fetch from server)
    const media: ExportProjectData['media'] = [];
    try {
      const mediaResult = await this.listMedia();
      for (const item of mediaResult.media) {
        try {
          const mediaUrl = this.getMediaUrl(item.filename);
          const response = await fetch(mediaUrl, {
            headers: { 'Authorization': `Bearer ${this.token}` }
          });
          if (response.ok) {
            const blob = await response.blob();
            media.push({
              filename: item.filename,
              originalName: item.originalName,
              mimeType: item.mimeType,
              size: item.size,
              width: item.width ?? null,
              height: item.height ?? null,
              uploadedBy: item.uploadedBy ?? null,
              data: blob,
            });
          }
        } catch (err) {
          console.warn(`Failed to fetch media ${item.filename}:`, err);
        }
      }
    } catch (err) {
      console.warn('Failed to list media:', err);
    }

    // Build export using shared utility
    const exportData: ExportProjectData = {
      name: this.currentProjectName ?? 'project',
      files,
      history,
      media,
    };

    const blob = await generateExportBlob(exportData, {
      onWarning: (msg) => console.warn(msg),
    });

    // Trigger download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFilename(this.currentProjectName ?? 'project')}.leedoc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return { success: true };
  }

  async importProject(file: File, options: ImportOptions = {}): Promise<ImportResult> {
    const zip = await JSZip.loadAsync(file);

    let metadataFile = zip.file('project.json');
    let basePath = '';

    if (!metadataFile) {
      const files = Object.keys(zip.files);
      const projectJsonPath = files.find(f => f.endsWith('project.json') && !f.includes('_generated/'));
      if (projectJsonPath) {
        metadataFile = zip.file(projectJsonPath);
        basePath = projectJsonPath.replace('project.json', '');
      }
    }

    let projectName: string;
    if (metadataFile) {
      const metadata = JSON.parse(await metadataFile.async('string'));
      projectName = metadata.name || file.name.replace('.leedoc', '');
    } else {
      projectName = file.name.replace('.leedoc', '').replace('.zip', '');
    }

    if (!options.existingProjectId) {
      const projects = await this.listProjects();
      const existingProject = projects.find(p => p.name === projectName);
      if (existingProject) {
        return {
          success: false,
          projectName,
          existingProject,
          needsConfirmation: true
        };
      }
    }

    if (options.existingProjectId) {
      await this.deleteProject(options.existingProjectId);
    }

    const createResult = await this.apiFetch<{ id: string }>('/api/cloud/project', {
      method: 'POST',
      body: JSON.stringify({ name: projectName })
    });

    const projectId = createResult.id;
    this.currentProjectId = projectId;
    this.currentProjectName = projectName;
    this.files.clear();
    this.components.clear();
    this.fileHistory.clear();

    const uploadPromises: Promise<void>[] = [];

    zip.forEach((relativePath, zipEntry) => {
      if (relativePath.endsWith('project.json')) return;
      if (zipEntry.dir) return;
      if (relativePath.includes('_generated/')) return;

      const filename = relativePath.split('/').pop() ?? '';
      if (filename.startsWith('._') || filename === '.DS_Store' || relativePath.includes('__MACOSX/')) return;

      let cleanPath = relativePath;
      if (basePath && cleanPath.startsWith(basePath)) {
        cleanPath = cleanPath.slice(basePath.length);
      }

      if (cleanPath.startsWith('_components/') || cleanPath.endsWith('.src.md')) {
        uploadPromises.push(
          zipEntry.async('string').then(async content => {
            if (cleanPath.startsWith('_components/')) {
              this.components.set(cleanPath.replace('_components/', ''), content);
            } else {
              this.files.set(cleanPath, content);
            }
            await this.apiFetch(`/api/cloud/project/${projectId}/file`, {
              method: 'PUT',
              body: JSON.stringify({ path: cleanPath, content })
            });
          })
        );
      } else if (cleanPath.startsWith('_history/') && cleanPath.endsWith('.json')) {
        uploadPromises.push(
          (async () => {
            const metaContent = await zipEntry.async('string');
            const metadata = JSON.parse(metaContent);

            const pathWithoutHistory = cleanPath.replace('_history/', '').replace('.json', '');
            const lastSlashIndex = pathWithoutHistory.lastIndexOf('/');
            if (lastSlashIndex === -1) return;

            const filePath = pathWithoutHistory.substring(0, lastSlashIndex);
            const snapshotId = pathWithoutHistory.substring(lastSlashIndex + 1);

            const contentPath = `_history/${filePath}/${snapshotId}.md`;
            let contentZipPath = contentPath;
            if (basePath) contentZipPath = basePath + contentPath;

            const contentEntry = zip.file(contentZipPath);
            if (!contentEntry) return;

            const content = await contentEntry.async('string');

            if (!this.fileHistory.has(filePath)) {
              this.fileHistory.set(filePath, []);
            }
            this.fileHistory.get(filePath)!.push({
              id: snapshotId,
              timestamp: metadata.timestamp,
              reason: metadata.reason,
              content
            });

            await this.apiFetch(`/api/cloud/project/${projectId}/history`, {
              method: 'POST',
              body: JSON.stringify({
                filePath,
                snapshotId,
                reason: metadata.reason,
                content
              })
            });
          })()
        );
      }
    });

    await Promise.all(uploadPromises);

    for (const snapshots of this.fileHistory.values()) {
      snapshots.sort((a, b) => b.timestamp - a.timestamp);
    }

    // Import media files
    const manifestPath = basePath ? `${basePath}_media/manifest.json` : '_media/manifest.json';
    const manifestFile = zip.file(manifestPath);
    if (manifestFile) {
      try {
        const manifestContent = await manifestFile.async('string');
        const manifest = JSON.parse(manifestContent) as Array<{
          filename: string;
          originalName: string;
          mimeType: string;
          uploadedBy?: string;
        }>;

        for (const item of manifest) {
          const mediaPath = basePath ? `${basePath}_media/${item.filename}` : `_media/${item.filename}`;
          const mediaEntry = zip.file(mediaPath);
          if (mediaEntry) {
            try {
              const blob = await mediaEntry.async('blob');
              const file = new File([blob], item.originalName, { type: item.mimeType });
              await this.uploadMedia(file, item.uploadedBy);
            } catch (err) {
              console.warn(`Failed to import media ${item.filename}:`, err);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to import media manifest:', err);
      }
    }

    return { success: true, projectId, projectName };
  }

  // ==================== File-Level Version History ====================

  private getFileContent(filePath: string): string | null {
    if (!this.currentProjectId) return null;

    if (filePath.startsWith('_components/')) {
      const name = filePath.replace('_components/', '');
      return this.components.get(name) ?? null;
    } else {
      return this.files.get(filePath) ?? null;
    }
  }

  async createSnapshot(filePath: string, reason: SnapshotReason): Promise<CreateSnapshotResult> {
    if (!this.currentProjectId) {
      return { success: false, error: 'No project selected' };
    }

    const content = this.getFileContent(filePath);
    if (content === null) {
      return { success: false, error: 'File not found' };
    }

    const timestamp = Date.now();
    const snapshotId = generateSnapshotId(timestamp);

    if (!this.fileHistory.has(filePath)) {
      this.fileHistory.set(filePath, []);
    }

    this.fileHistory.get(filePath)!.unshift({
      id: snapshotId,
      timestamp,
      reason,
      content
    });

    try {
      await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/history`, {
        method: 'POST',
        body: JSON.stringify({
          filePath,
          snapshotId,
          reason,
          content
        })
      });
    } catch (err) {
      console.warn('Failed to sync snapshot to server:', err);
    }

    await this.pruneSnapshots(filePath);

    return { success: true, snapshotId };
  }

  async listSnapshots(filePath: string): Promise<SnapshotMetadata[]> {
    if (!this.currentProjectId || !filePath) {
      return [];
    }

    const snapshots = this.fileHistory.get(filePath) ?? [];
    return snapshots.map(s => ({
      id: s.id,
      timestamp: s.timestamp,
      reason: s.reason
    }));
  }

  async getSnapshot(filePath: string, snapshotId: string): Promise<Snapshot | null> {
    if (!this.currentProjectId) {
      return null;
    }

    const snapshots = this.fileHistory.get(filePath) ?? [];
    return snapshots.find(s => s.id === snapshotId) ?? null;
  }

  async deleteSnapshot(filePath: string, snapshotId: string): Promise<DeleteResult> {
    if (!this.currentProjectId) {
      return { success: false };
    }

    const snapshots = this.fileHistory.get(filePath);
    if (snapshots) {
      const index = snapshots.findIndex(s => s.id === snapshotId);
      if (index !== -1) {
        snapshots.splice(index, 1);
      }
    }

    return { success: true };
  }

  private async getLastSnapshot(filePath: string): Promise<SnapshotMetadata | null> {
    const snapshots = this.fileHistory.get(filePath) ?? [];
    return snapshots[0] ?? null;
  }

  async maybeCreateSnapshot(filePath: string): Promise<void> {
    if (!this.currentProjectId || !filePath) return;

    const lastSnapshot = await this.getLastSnapshot(filePath);
    const reason = shouldCreateSnapshot(lastSnapshot);

    if (reason) {
      await this.createSnapshot(filePath, reason);
    }
  }

  private async pruneSnapshots(filePath: string): Promise<void> {
    const snapshots = this.fileHistory.get(filePath);
    if (!snapshots || snapshots.length <= 5) return;

    const keptSliced = applyRetentionPolicy(snapshots);
    this.fileHistory.set(filePath, keptSliced);

    const keepIds = keptSliced.map(s => s.id);
    try {
      await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/history/prune`, {
        method: 'DELETE',
        body: JSON.stringify({ filePath, keepIds })
      });
    } catch (err) {
      console.warn('Failed to sync snapshot pruning to server:', err);
    }
  }

  async restoreSnapshot(filePath: string, snapshotId: string): Promise<RestoreSnapshotResult> {
    if (!this.currentProjectId) {
      return { success: false, error: 'No project selected' };
    }

    const snapshot = await this.getSnapshot(filePath, snapshotId);
    if (!snapshot) {
      return { success: false, error: 'Snapshot not found' };
    }

    if (filePath.startsWith('_components/')) {
      const name = filePath.replace('_components/', '');
      this.components.set(name, snapshot.content);
    } else {
      this.files.set(filePath, snapshot.content);
    }

    await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/file`, {
      method: 'PUT',
      body: JSON.stringify({ path: filePath, content: snapshot.content })
    });

    return { success: true, content: snapshot.content };
  }

  // ==================== Media Management ====================

  async uploadMedia(file: File, uploadedBy?: string): Promise<MediaUploadResult> {
    if (!this.currentProjectId) {
      return { success: false, error: 'No project selected' };
    }

    const formData = new FormData();
    formData.append('file', file);
    if (uploadedBy) {
      formData.append('uploadedBy', uploadedBy);
    }

    // Use fetch directly for FormData (don't set Content-Type, let browser set boundary)
    const res = await fetch(`/api/cloud/project/${this.currentProjectId}/media`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { success: false, error: data.error || `Upload failed: ${res.status}` };
    }

    const result = await res.json() as MediaUploadResult;

    // Cache the signed URL for this media
    if (result.success && result.filename && result.signedUrl) {
      this.signedMediaUrls.set(result.filename, result.signedUrl);
    }

    return result;
  }

  async listMedia(): Promise<MediaListResult> {
    if (!this.currentProjectId) {
      return { media: [], count: 0, totalSize: 0 };
    }

    const result = await this.apiFetch<MediaListResult>(`/api/cloud/project/${this.currentProjectId}/media`);

    // Cache all signed URLs
    for (const item of result.media) {
      if (item.signedUrl) {
        this.signedMediaUrls.set(item.filename, item.signedUrl);
      }
    }

    return result;
  }

  async getMediaUsage(filename: string): Promise<MediaUsage> {
    if (!this.currentProjectId) {
      return { filename, usedIn: [], isOrphaned: true };
    }

    return this.apiFetch<MediaUsage>(
      `/api/cloud/project/${this.currentProjectId}/media/${encodeURIComponent(filename)}/usage`
    );
  }

  async deleteMedia(filename: string): Promise<MediaDeleteResult> {
    if (!this.currentProjectId) {
      return { success: false };
    }

    await this.apiFetch(
      `/api/cloud/project/${this.currentProjectId}/media/${encodeURIComponent(filename)}`,
      { method: 'DELETE' }
    );

    return { success: true, deleted: 1 };
  }

  async bulkDeleteMedia(filenames: string[]): Promise<MediaDeleteResult> {
    if (!this.currentProjectId || filenames.length === 0) {
      return { success: false, deleted: 0 };
    }

    const result = await this.apiFetch<{ deleted: number }>(
      `/api/cloud/project/${this.currentProjectId}/media`,
      {
        method: 'DELETE',
        body: JSON.stringify({ filenames })
      }
    );

    return { success: true, deleted: result.deleted };
  }

  getMediaUrl(filename: string): string {
    if (!this.currentProjectId) return '';

    // Return cached signed URL if available
    const signedUrl = this.signedMediaUrls.get(filename);
    if (signedUrl) {
      return signedUrl;
    }

    // Fallback to unsigned URL (will fail without signature, but needed for export)
    return `/api/cloud/project/${this.currentProjectId}/media/${encodeURIComponent(filename)}`;
  }

  /**
   * Refresh signed URLs by fetching the media list
   * Call this if URLs are expired or missing
   */
  async refreshSignedUrls(): Promise<void> {
    await this.listMedia();
  }

  // ==================== Checklist Mode ====================

  async getChecklist(filePath: string): Promise<string[] | null> {
    if (!this.checklistCache.has(filePath)) {
      return null;
    }
    return this.checklistCache.get(filePath)!;
  }

  async saveChecklist(filePath: string, checkedSlugs: string[]): Promise<void> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    this.checklistCache.set(filePath, checkedSlugs);

    await this.apiFetch(`/api/cloud/project/${this.currentProjectId}/checklist`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filePath, checkedSlugs }),
    });
  }

  async deleteChecklist(filePath: string): Promise<void> {
    if (!this.currentProjectId) {
      throw new Error('No project selected');
    }

    this.checklistCache.delete(filePath);

    await this.apiFetch(
      `/api/cloud/project/${this.currentProjectId}/checklist?path=${encodeURIComponent(filePath)}`,
      { method: 'DELETE' }
    );
  }
}
